// Experience Gallery — browse curated experiences (Campfire, Studio, Podcast,
// Concert, Group Room) and clone one into your own editable world.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Copy, Pencil } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../config/supabase';
import GathrBot from '../components/GathrBot';
import {
  fetchExperiences,
  cloneExperience,
  experienceMeta,
  EXPERIENCE_TYPES,
  type ExperienceWorld,
  type ExperienceType,
} from '../services/experienceService';
import './ExperiencesPage.css';

const ExperiencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState<ExperienceWorld[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ExperienceType | 'all'>('all');
  const [cloning, setCloning] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSignedIn(!!data.session?.user);
      setUserId(data.session?.user?.id || null);
    });
    fetchExperiences()
      .then(setExperiences)
      .catch((e) => setError(e?.message || 'Could not load experiences'))
      .finally(() => setLoading(false));
  }, []);

  // Which type chips to show — only kinds that actually have experiences.
  const availableTypes = useMemo(() => {
    const present = new Set(experiences.map((e) => e.experience_type));
    return EXPERIENCE_TYPES.filter((t) => present.has(t.type));
  }, [experiences]);

  const shown = filter === 'all' ? experiences : experiences.filter((e) => e.experience_type === filter);

  const useExperience = async (exp: ExperienceWorld) => {
    if (!signedIn) { navigate('/dashboard'); return; }
    setError(null);
    setCloning(exp.id);
    try {
      const newId = await cloneExperience(exp.id, exp.name);
      navigate(`/world/${newId}?name=${encodeURIComponent(exp.name)}`);
    } catch (e: any) {
      setError(e?.message || 'Could not create your copy');
      setCloning(null);
    }
  };

  const preview = (exp: ExperienceWorld) => navigate(`/vr/${exp.id}?name=${encodeURIComponent(exp.name)}`);

  // Owner-only: open the starter template in the builder to edit it directly.
  const editExperience = (exp: ExperienceWorld) => navigate(`/world/${exp.id}?name=${encodeURIComponent(exp.name)}`);

  return (
    <div className="xp-page">
      <PageHeader
        title="Experiences"
        accent="Ready-made spaces."
        subtitle="Pick a venue, make it your own in one click, then invite people in. Every experience becomes a full world you own and can edit."
      />

      <div className="xp-shell ph-container">

        {error && <div className="xp-error">{error}</div>}

        {availableTypes.length > 0 && (
          <div className="xp-filters">
            <button className={`xp-chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
            {availableTypes.map((t) => (
              <button key={t.type} className={`xp-chip ${filter === t.type ? 'active' : ''}`} onClick={() => setFilter(t.type)}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="xp-muted">Loading experiences…</p>
        ) : shown.length === 0 ? (
          <div className="xp-empty">
            <GathrBot size={72} variant="brand" float />
            <h3>No experiences published yet</h3>
            <p className="xp-muted">Build a world, then publish it as an experience from your dashboard — it’ll show up here for everyone to clone.</p>
          </div>
        ) : (
          <div className="xp-grid">
            {shown.map((exp) => {
              const meta = experienceMeta(exp.experience_type);
              return (
                <div key={exp.id} className="xp-card">
                  <button className="xp-thumb" onClick={() => preview(exp)} aria-label={`Preview ${exp.name}`}>
                    {exp.thumbnail_url ? (
                      <img src={exp.thumbnail_url} alt={exp.name} loading="lazy" />
                    ) : (
                      <div className="xp-thumb-fallback"><GathrBot size={54} variant="brand" float /></div>
                    )}
                    <span className="xp-type">{meta.label}</span>
                    <span className="xp-preview-hint"><Play size={13} /> Preview</span>
                  </button>
                  <div className="xp-card-body">
                    <div className="xp-name">{exp.name}</div>
                    <div className="xp-blurb">{meta.blurb}</div>
                    <div className="xp-actions">
                      <button className="xp-use" onClick={() => useExperience(exp)} disabled={cloning === exp.id}>
                        <Copy size={15} /> {cloning === exp.id ? 'Creating your copy…' : 'Use this experience'}
                      </button>
                      {exp.owner_id === userId && (
                        <button className="xp-edit" onClick={() => editExperience(exp)} title="Edit this starter template">
                          <Pencil size={15} /> Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperiencesPage;
